import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ContributeForm from '../../form/ContributeForm/ContributeForm';
import { DEFAULT_GRID_COLUMNS } from '../../../constants/grid';
import Grid from '../../organisms/Grid/Grid';
import {
  buildFormTree,
  getNodeInitialValues,
  buildFormOutput,
  buildAnswer,
  buildAnswers,
  getReviewTree,
  buildBlockAnswerChoices,
  clearHiddenValues,
  computeComputedHeights,
} from '../../../helpers/formTree';
import ContributeFormNode from './../ContributeFormNode/ContributeFormNode';
import Button from '../../atoms/Button/Button';
import useTranslate from '../../../hooks/useTranslate';
import { flatten } from '../../../helpers/array';
import { DEFAULT_GRID_ROWHEIGHT, GRID_GUTTER } from '../../../constants/grid';
import styles from './ContributeFormTemplate.css';
import { useDispatch, useSelector } from 'react-redux';
import compare from '../../../helpers/compare';
import { submit, getFormValues, getFormSyncErrors, initialize } from 'redux-form';
import Timer from '../../atoms/Timer/Timer';
import { compileAllRules, boxHiddenRule, evaluateAllRules } from '../../../helpers/formula';
import ContributeFormErrorModal from '../ContributeFormErrorModal/ContributeFormErrorModal';
import { createToast } from '../../../store/actions/toast';
import { ERROR } from '../../../constants/toastType';
import HiddenBoxesContext from './HiddenBoxesContext';
import ComputedHeightsContext from './ComputedHeightsContext';
import KeyboardShortcutsProbe from '../../atoms/KeyboardShortcutsProbe/KeyboardShortcutsProbe';
import { formQualitySelector } from '../../../store/selectors/editForm';
import FormStyle from '../../atoms/FormStyle';
import PDFOCRGeolocProbe from '../../atoms/PDFOCRGeolocProbe/PDFOCRGeolocProbe';
import { PdfOcrViewerProvider } from '../../organisms/PdfOcrViewer/PdfOcrViewerContext';
import { OcrMappingProvider } from '../../../contexts/OcrMappingContext';

const focusErrorField = (errors = []) => {
  try {
    if (errors?.length) {
      const name = errors[0];
      const element = document.querySelector(`[name$="${name}"]`);
      if (element != null) {
        element.focus();
      }
    }
    // eslint-disable-next-line no-empty, no-unused-vars
  } catch (err) { }
};

const unifyKeysErrors = (input, prefix = '') => {
  let result = [];

  for (const key in input) {
    const value = input[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value?.$items)) {
      value.$items.forEach((item, index) => {
        result = result.concat(unifyKeysErrors(item, `${newKey}.$items[${index}]`));
      });
    } else {
      result.push(newKey);
    }
  }

  return result;
};

const ContributeFormTemplate = ({
  data,
  form,
  answers = null,
  answer = null,
  isReview,
  onSubmit,
  choicesOverrides,
  taskExpiresAt,
  minTimeElapsed = true,
}) => {
  const i18n = useTranslate();
  const dispatch = useDispatch();
  const { allowError } = form;
  const [flattenedSyncErrors, setFlattenedSyncErrors] = useState(null);
  const formSyncErrors = useSelector(getFormSyncErrors('contribute'), compare);
  const values = useSelector(getFormValues('contribute'), compare);
  const { strategy } = useSelector(formQualitySelector);

  const [validationErrors, setValidationErrors] = useState(null);

  const gridColumns = form?.gridSize || DEFAULT_GRID_COLUMNS;
  const gridRowHeight = (DEFAULT_GRID_COLUMNS * DEFAULT_GRID_ROWHEIGHT) / form?.gridSize;
  const minHeightRef = useRef(null);

  // Build the form tree from blocks & fields
  const formTree = useMemo(
    () =>
      buildFormTree(form, {
        choicesOverrides,
      }),
    [form, choicesOverrides],
  );
  const validationRules = useMemo(() => compileAllRules(form.validationRules, formTree), [
    form,
    formTree,
  ]);
  const hideRules = useMemo(() => compileAllRules(formTree.hideRules, formTree), [
    formTree,
  ]);

  const { hiddenBoxes, failedHideExecutions } = useMemo(() => {
    // If redux-form has not already set it's values with the "initialize" dispatch, we return
    if (values === undefined) {
      return { hiddenBoxes: [], failedHideExecutions: [] };
    }
    const [errors, failedExecutions] = boxHiddenRule(hideRules, values);
    const boxesToHide = [
      ...new Set(errors.reduce((boxes, error) => [error.fieldPath, ...boxes], [])),
    ];
    return { hiddenBoxes: boxesToHide, failedHideExecutions: failedExecutions };
  }, [hideRules, values]);

  // Compute heights for all elements
  const computedHeights = useMemo(() => {
    return computeComputedHeights(formTree, hiddenBoxes, values);
  }, [formTree, hiddenBoxes, values]);

  // Show hide-rule execution errors in a separate effect
  useEffect(() => {
    if (failedHideExecutions.length) {
      createToast(
        ERROR,
        i18n('ContributeFormTemplate.hideFailedExecution', { count: failedHideExecutions.length }),
      );
    }
  }, [failedHideExecutions.length, i18n]);

  // Update scrollable zone min height based on computed heights
  useEffect(() => {
    const rootData = computedHeights.get('root');
    if (!rootData) return;

    const scrollableZone = document.getElementById('scrollable-zone');
    if (scrollableZone) {
      const minHeight = rootData.height * gridRowHeight + gridRowHeight * 2;
      if (minHeightRef.current !== minHeight) {
        minHeightRef.current = minHeight;
        scrollableZone.style.minHeight = `${minHeight}px`;
      }
    }
  }, [computedHeights, gridRowHeight]);

  // Submit without validation checks
  const doSubmit = useCallback(
    (values) => {
      if (!minTimeElapsed) {
        alert("Le temps minimum n'est pas écoulé !");
        return;
      }

      if (hiddenBoxes.length) {
        const clearedValues = clearHiddenValues(values, hiddenBoxes);
        dispatch(initialize('contribute', clearedValues, true));
      }

      onSubmit(buildFormOutput(formTree, values, hiddenBoxes));
    },
    [onSubmit, formTree, hiddenBoxes, minTimeElapsed],
  );

  const processValidationRules = useCallback(
    (values) => {
      const finalValues = hiddenBoxes.length
        ? clearHiddenValues(values, hiddenBoxes) : values;
      const [errors, failedExecutions] = evaluateAllRules(validationRules, finalValues);

      if (failedExecutions.length) {
        createToast(
          ERROR,
          i18n('ContributeFormTemplate.failedExecution', { count: failedExecutions.length }),
        );
      }

      if (errors.length) {
        setValidationErrors(errors);
      }

      return { errors, failedExecutions, finalValues };
    },
    [validationRules, hiddenBoxes],
  );

  const handleSubmit = useCallback(() => {
    if (allowError && Object.keys(formSyncErrors).length) {
      processValidationRules(values);
      setFlattenedSyncErrors(Object.values(flatten(formSyncErrors)).filter((er) => er));
    }
    focusErrorField(unifyKeysErrors(formSyncErrors));
    dispatch(submit('contribute'));
  }, [values, formTree, formSyncErrors, allowError, doSubmit]);

  // Submit WITH validation checks
  const onSubmitTransformHandler = useCallback(
    (values) => {
      const { finalValues, failedExecutions, errors } = processValidationRules(values);
      if (!errors.length && !failedExecutions.length) {
        doSubmit(finalValues);
      }
    },
    [doSubmit, processValidationRules],
  );

  // Generate confidence tree for multi-review
  const reviewTree = useMemo(() => (answer ? getReviewTree(answer, form.blocks) : null), [answer]);

  // Generate answer choices for [multi-]review
  const answersValues = useMemo(
    () => (answers && strategy === 'multi' ? buildAnswers(answers) : null),
    [answers],
  );
  const blocksValues = useMemo(
    () => (answer && strategy === 'multi' ? buildBlockAnswerChoices(answer) : null),
    [answer],
  );

  useEffect(() => {
    const main = document.getElementById('app-main');
    main.dataset.page = 'contribute';

    dispatch(initialize('contribute', {
      ...data,
      ...(answer ? buildAnswer(answer) : getNodeInitialValues(formTree)),
    }));

    return () => {
      if (main) {
        delete main.dataset.page;
      }
    };
  }, []);

  const preventEnterSubmit = useCallback(
    (event) => {
      if (event.keyCode === 13 && !form.enterToValidate) {
        // Prevent default enter behavior on form-level if enter to validate is not enabled
        event.preventDefault();
      }
    },
    [form.enterToValidate],
  );

  // If there is no values we don't render -> values from redux-form are
  // needed first to have computedHeights and hiddenBoxes computed
  if (values === undefined) return null;

  return (
    <OcrMappingProvider initialAnswer={answer}>
      <PdfOcrViewerProvider>
        <FormStyle form={form}>
          {form.keyboardShortcuts && (
            <KeyboardShortcutsProbe shortcuts={form.keyboardShortcuts} form={form} />
          )}

          <PDFOCRGeolocProbe form={form} answer={answer} />

          {form.showFormTitle === true ? (
            <div className={styles.ContributeFormTemplate__title}>{form.name}</div>
          ) : null}

          <ComputedHeightsContext.Provider value={computedHeights}>
            <HiddenBoxesContext.Provider value={hiddenBoxes}>
              <ContributeFormErrorModal
                open={(
                  validationErrors && !!validationErrors.length) || !!flattenedSyncErrors?.length
                }
                errors={validationErrors}
                syncErrors={flattenedSyncErrors}
                onClose={() => {
                  setValidationErrors(null);
                  setFlattenedSyncErrors(null);
                }}
                onForceSubmit={
                  form.allowError &&
                  (() => {
                    setValidationErrors(null);
                    setFlattenedSyncErrors(null);
                    doSubmit(values);
                  })
                }
              />
              <div className={styles.ContributeFormTemplate} id="form_root">
                {/* scale the grid container so parent overflow-y wirks properly */}
                <div id="scrollable-zone"
                  style={{
                    minHeight: formTree.totalHeight * gridRowHeight + gridRowHeight * 2,
                  }}
                >
                  <ContributeForm
                    onSubmit={onSubmitTransformHandler}
                    onKeyDown={preventEnterSubmit}
                  >
                    {/* If enter to validate is enabled, we need to add this invisible submit
                     button that "enables" the feature */}
                    {form.enterToValidate && (
                      <button
                        type="submit"
                        className={styles.ContributeFormTemplate__hiddenSubmit}
                      ></button>
                    )}
                    <Grid
                      columns={gridColumns}
                      rowHeight={gridRowHeight}
                      gutter={GRID_GUTTER}
                      dotBackground={false}
                    >
                      <ContributeFormNode
                        node={formTree}
                        answersValues={answersValues}
                        blocksValues={blocksValues}
                        reviewTree={reviewTree}
                        isReview={isReview}
                      />
                    </Grid>
                  </ContributeForm>
                </div>
                <div className={styles.ContributeFormTemplate__actions}>
                  <div className={styles.ContributeFormTemplate__actions_meta} />
                  <Button
                    rounded
                    onClick={handleSubmit}
                    className={styles.ContributeFormTemplate__submit}
                  >
                    {form.submitLabel || i18n('ContributeFormTemplate.submit')}
                  </Button>
                  <div className={styles.ContributeFormTemplate__actions_meta}>
                    <Timer expiresAt={taskExpiresAt}></Timer>
                  </div>
                </div>
              </div>
            </HiddenBoxesContext.Provider>
          </ComputedHeightsContext.Provider>
        </FormStyle>
      </PdfOcrViewerProvider>
    </OcrMappingProvider>
  );
};

ContributeFormTemplate.propTypes = {
  form: PropTypes.object,
  onSubmit: PropTypes.func,
  data: PropTypes.object,
  answer: PropTypes.object,
  answers: PropTypes.object,
  choicesOverrides: PropTypes.object,
  minTimeElapsed: PropTypes.bool,
  taskExpiresAt: PropTypes.number,
  isReview: PropTypes.bool,
};

export default ContributeFormTemplate;
